// internal/FichesStudioPage.jsx — Gestion Owner intégrale des fiches Search : lieux, mairies et commerçants.
// Modification, masquage, mise en avant (commerçants), upload photo, historique versionné avec retour arrière.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Landmark, UserRound, Store, Search, Eye, EyeOff, Pencil, RotateCcw, ExternalLink, X, Star, Upload, History } from "lucide-react";
import InternalLayout from "@/internal/InternalLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const hdrs = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });

const F = {
  card: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(46,226,123,0.14)", borderRadius: 14, padding: 14 },
  tab: (on) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 999, border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 800, background: on ? "linear-gradient(180deg,#3BE07A,#1DB954)" : "rgba(255,255,255,0.06)", color: on ? "#06281A" : "#CFE9DA" }),
  row: { display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderBottom: "1px solid rgba(255,255,255,0.06)" },
  badge: (bg, c) => ({ fontSize: 9.5, fontWeight: 900, borderRadius: 999, padding: "2px 8px", background: bg, color: c }),
  act: { display: "inline-flex", alignItems: "center", gap: 4, border: "1px solid rgba(46,226,123,0.3)", background: "rgba(46,226,123,0.07)", color: "#2EE27B", fontSize: 10.5, fontWeight: 800, borderRadius: 8, padding: "5px 9px", cursor: "pointer" },
  input: { width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 9, padding: "8px 10px", color: "#EAF6EE", fontSize: 12, outline: "none" },
  lbl: { fontSize: 10.5, fontWeight: 800, color: "#8FB8A2", margin: "9px 0 4px", display: "block" },
};

const PLACE_FIELDS = [
  ["type_label", "Sous-titre (type affiché)", "input"],
  ["desc", "Description courte", "textarea"],
  ["side_note", "Suite de la description (dérouler)", "textarea"],
  ["history", "Histoire (registre)", "textarea"],
  ["image", "Photo du héro", "photo"],
  ["tags", "Tags (séparés par des virgules)", "input"],
];
const MAIRIE_FIELDS = [
  ["maire_name", "Nom du maire", "input"],
  ["maire_parti", "Parti", "input"],
  ["maire_mandat", "Mandat", "input"],
  ["maire_note", "Note sous la présentation", "textarea"],
  ["population", "Population", "input"],
  ["superficie", "Superficie", "input"],
  ["statut", "Statut", "input"],
  ["tagline", "Devise / citation du héro", "textarea"],
  ["presentation", "Présentation", "textarea"],
  ["hero", "Photo du héro", "photo"],
];
const MERCHANT_FIELDS = [
  ["name", "Nom affiché", "input"],
  ["category_label", "Catégorie affichée", "input"],
  ["image", "Photo", "photo"],
];
const FIELDS = { place: PLACE_FIELDS, mairie: MAIRIE_FIELDS, merchant: MERCHANT_FIELDS };

const fmtDate = (d) => new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export default function FichesStudioPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("places");
  const [data, setData] = useState({ places: [], mairies: [] });
  const [merchants, setMerchants] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null); // { kind, slug, base, patch, hidden, boost, label }
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const photoField = useRef(null);

  const load = () => fetch(`${API}/fiches-studio`, { headers: hdrs() }).then((r) => r.json())
    .then((j) => j.ok ? setData({ places: j.places, mairies: j.mairies }) : toast.error("Accès réservé à l'owner"))
    .catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);

  const loadMerchants = (query) => fetch(`${API}/fiches-studio-merchants?q=${encodeURIComponent(query || "")}`, { headers: hdrs() })
    .then((r) => r.json()).then((j) => setMerchants(j.ok ? j.merchants : [])).catch(() => setMerchants([]));
  useEffect(() => {
    if (tab !== "merchants") return;
    const t = setTimeout(() => loadMerchants(q), 350);
    return () => clearTimeout(t);
  }, [tab, q]);

  const list = useMemo(() => {
    if (tab === "merchants") return merchants;
    const src = tab === "places" ? data.places : data.mairies;
    const n = q.trim().toLowerCase();
    return n ? src.filter((x) => `${x.name} ${x.slug} ${x.maire || ""}`.toLowerCase().includes(n)) : src;
  }, [data, merchants, tab, q]);

  const kindOf = () => (tab === "places" ? "place" : tab === "mairies" ? "mairie" : "merchant");

  const openEdit = async (kind, slug, label) => {
    const j = await fetch(`${API}/fiches-studio/${kind}/${slug}`, { headers: hdrs() }).then((r) => r.json()).catch(() => null);
    if (!j?.ok) return toast.error("Fiche introuvable");
    const patch = { ...j.patch };
    if (kind === "place" && Array.isArray(patch.tags)) patch.tags = patch.tags.join(", ");
    setEdit({ kind, slug, base: j.base, patch, hidden: j.hidden, boost: !!j.boost, label: label || "" });
    fetch(`${API}/fiches-studio/${kind}/${slug}/versions`, { headers: hdrs() }).then((r) => r.json())
      .then((v) => setVersions(v.ok ? v.versions : [])).catch(() => setVersions([]));
  };

  const putFiche = async (kind, slug, body) => {
    const j = await fetch(`${API}/fiches-studio/${kind}/${slug}`, { method: "PUT", headers: hdrs(), body: JSON.stringify(body) }).then((r) => r.json()).catch(() => null);
    return !!j?.ok;
  };

  const save = async () => {
    if (!edit) return;
    setSaving(true);
    const ok = await putFiche(edit.kind, edit.slug, { patch: edit.patch, hidden: edit.hidden, boost: edit.boost, label: edit.label });
    setSaving(false);
    if (!ok) return toast.error("Enregistrement impossible");
    toast.success("Fiche mise à jour");
    setEdit(null);
    tab === "merchants" ? loadMerchants(q) : load();
  };

  const quickToggle = async (x, changes) => {
    const kind = kindOf();
    const j = await fetch(`${API}/fiches-studio/${kind}/${x.slug || x.id}`, { headers: hdrs() }).then((r) => r.json()).catch(() => null);
    if (!j?.ok) return;
    const patch = { ...j.patch };
    if (kind === "place" && Array.isArray(patch.tags)) patch.tags = patch.tags.join(", ");
    const ok = await putFiche(kind, x.slug || x.id, { patch, hidden: j.hidden, boost: !!j.boost, label: x.name, ...changes });
    if (ok) toast.success(changes.hidden !== undefined ? (changes.hidden ? "Masquée du Search" : "Visible dans le Search") : (changes.boost ? "Mise en avant dans les résultats" : "Mise en avant retirée"));
    tab === "merchants" ? loadMerchants(q) : load();
  };

  const reset = async (kind, slug) => {
    await fetch(`${API}/fiches-studio/${kind}/${slug}`, { method: "DELETE", headers: hdrs() });
    toast.success("Fiche réinitialisée (contenu d'origine)");
    setEdit(null);
    tab === "merchants" ? loadMerchants(q) : load();
  };

  const revert = async (versionId) => {
    const j = await fetch(`${API}/fiches-studio/${edit.kind}/${edit.slug}/revert`, { method: "POST", headers: hdrs(), body: JSON.stringify({ version_id: versionId }) }).then((r) => r.json()).catch(() => null);
    if (!j?.ok) return toast.error("Restauration impossible");
    toast.success("Version restaurée");
    setEdit(null);
    tab === "merchants" ? loadMerchants(q) : load();
  };

  const pickPhoto = (field) => { photoField.current = field; fileRef.current?.click(); };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image trop lourde (5 Mo max)");
    const reader = new FileReader();
    reader.onload = async () => {
      const j = await fetch(`${API}/fiches-studio/upload-photo`, { method: "POST", headers: hdrs(), body: JSON.stringify({ image: reader.result }) }).then((r) => r.json()).catch(() => null);
      if (!j?.ok) return toast.error("Téléversement impossible");
      setEdit((ed) => ({ ...ed, patch: { ...ed.patch, [photoField.current]: j.url } }));
      toast.success("Photo téléversée");
    };
    reader.readAsDataURL(f);
  };

  const baseVal = (k) => {
    if (!edit) return "";
    const b = edit.base;
    if (edit.kind === "place") return k === "tags" ? (b.tags || []).join(", ") : (b[k] || "");
    if (edit.kind === "merchant") return "";
    const map = { maire_name: b.maire?.name, maire_parti: b.maire?.parti, maire_mandat: b.maire?.mandat, maire_note: b.maire?.note, population: b.stats?.population, superficie: b.stats?.superficie, statut: b.stats?.statut, tagline: b.tagline, presentation: b.presentation, hero: b.hero };
    return map[k] || "";
  };

  return (
    <InternalLayout title="Fiches Search" subtitle="Gestion intégrale des résultats AYA Search : modifie, masque, mets en avant ou réinitialise les fiches lieux, institutions et commerçants. Historique complet avec retour arrière.">
      <div data-testid="fiches-studio-page">
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} data-testid="fs-file-input" />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <button style={F.tab(tab === "places")} data-testid="fs-tab-places" onClick={() => setTab("places")}><MapPin size={13} /> Lieux ({data.places.length})</button>
          <button style={F.tab(tab === "mairies")} data-testid="fs-tab-mairies" onClick={() => setTab("mairies")}><Landmark size={13} /> Mairies ({data.mairies.length})</button>
          <button style={F.tab(tab === "merchants")} data-testid="fs-tab-merchants" onClick={() => setTab("merchants")}><Store size={13} /> Commerçants</button>
          <button style={F.tab(false)} data-testid="fs-goto-personnalites" onClick={() => navigate("/internal/personnalites")}><UserRound size={13} /> Fiches AYA ID</button>
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999, padding: "7px 13px", minWidth: 230 }}>
            <Search size={13} color="#8FB8A2" />
            <input data-testid="fs-search" placeholder={tab === "merchants" ? "Rechercher un commerçant (ex : pharmacie riviera)…" : "Filtrer les fiches…"} value={q} onChange={(e) => setQ(e.target.value)} style={{ background: "transparent", border: "none", outline: "none", color: "#EAF6EE", fontSize: 12, flex: 1 }} />
          </div>
        </div>

        <div style={F.card}>
          {tab === "merchants" && !q.trim() && <div style={{ padding: "4px 10px 10px", fontSize: 11, color: "#8FB8A2" }}>Commerçants déjà gérés ci-dessous — recherche pour en gérer d'autres.</div>}
          {list.map((x) => {
            const id = x.slug || x.id;
            return (
              <div key={id} style={F.row} data-testid={`fs-row-${id}`}>
                {tab === "merchants" && (x.image ? <img src={x.image} alt="" style={{ width: 34, height: 34, borderRadius: 8, objectFit: "cover" }} /> : <Store size={18} color="#8FB8A2" />)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#EAF6EE" }}>{x.name}</div>
                  <div style={{ fontSize: 10.5, color: "#8FB8A2" }}>
                    {tab === "places" ? x.type : tab === "mairies" ? (x.maire ? `Maire : ${x.maire}` : "") : [x.category_label, x.commune].filter(Boolean).join(" · ")} · {id}
                  </div>
                </div>
                {x.boost && <span style={F.badge("rgba(255,200,60,0.16)", "#FFD166")}>EN AVANT</span>}
                {x.overridden && <span style={F.badge("rgba(59,224,122,0.15)", "#3BE07A")}>MODIFIÉE</span>}
                {x.hidden && <span style={F.badge("rgba(239,83,80,0.18)", "#FF8A80")}>MASQUÉE</span>}
                {tab !== "merchants" && (
                  <button style={F.act} data-testid={`fs-open-${id}`} onClick={() => window.open(tab === "places" ? `/search?q=${encodeURIComponent(id.replace(/-/g, " "))}` : `/mairie/${id}`, "_blank")}><ExternalLink size={11} /> Voir</button>
                )}
                {tab === "merchants" && (
                  <button style={{ ...F.act, ...(x.boost ? { background: "rgba(255,200,60,0.14)", borderColor: "rgba(255,200,60,0.4)", color: "#FFD166" } : {}) }} data-testid={`fs-boost-${id}`} onClick={() => quickToggle(x, { boost: !x.boost })}><Star size={11} fill={x.boost ? "currentColor" : "none"} /> {x.boost ? "Retirer" : "Mettre en avant"}</button>
                )}
                <button style={F.act} data-testid={`fs-edit-${id}`} onClick={() => openEdit(kindOf(), id, x.name)}><Pencil size={11} /> Modifier</button>
                <button style={F.act} data-testid={`fs-hide-${id}`} onClick={() => quickToggle(x, { hidden: !x.hidden })}>{x.hidden ? <Eye size={11} /> : <EyeOff size={11} />} {x.hidden ? "Afficher" : "Masquer"}</button>
                {(x.overridden || x.hidden || x.boost) && <button style={F.act} data-testid={`fs-reset-${id}`} onClick={() => reset(kindOf(), id)}><RotateCcw size={11} /> Réinit.</button>}
              </div>
            );
          })}
          {list.length === 0 && <div style={{ padding: 14, fontSize: 12, color: "#8FB8A2" }}>{tab === "merchants" ? "Aucun commerçant — lance une recherche ci-dessus." : "Aucune fiche ne correspond au filtre."}</div>}
        </div>

        {edit && (
          <div style={{ position: "fixed", inset: 0, zIndex: 80, background: "rgba(2,12,7,0.72)", display: "grid", placeItems: "center", padding: 16 }} onClick={() => setEdit(null)}>
            <div style={{ ...F.card, background: "#0B1F15", width: "min(660px, 100%)", maxHeight: "88vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()} data-testid="fs-edit-modal">
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <b style={{ color: "#EAF6EE", fontSize: 15, flex: 1 }}>{edit.kind === "place" ? edit.base.titles?.display : edit.kind === "mairie" ? edit.base.name : edit.label || edit.slug}</b>
                {edit.kind === "merchant" && (
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: edit.boost ? "#FFD166" : "#8FB8A2", fontWeight: 800, cursor: "pointer" }}>
                    <input type="checkbox" data-testid="fs-boost-toggle" checked={edit.boost} onChange={(e) => setEdit({ ...edit, boost: e.target.checked })} /> Mettre en avant
                  </label>
                )}
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: edit.hidden ? "#FF8A80" : "#8FB8A2", fontWeight: 800, cursor: "pointer" }}>
                  <input type="checkbox" data-testid="fs-hidden-toggle" checked={edit.hidden} onChange={(e) => setEdit({ ...edit, hidden: e.target.checked })} /> Masquer du Search
                </label>
                <button style={{ ...F.act, padding: "5px 7px" }} onClick={() => setEdit(null)}><X size={13} /></button>
              </div>
              <p style={{ fontSize: 10.5, color: "#8FB8A2", margin: "7px 0 2px" }}>Laisse un champ vide pour conserver le contenu d'origine affiché en dessous.</p>
              {FIELDS[edit.kind].map(([k, label, type]) => (
                <div key={k}>
                  <label style={F.lbl}>{label}</label>
                  {type === "textarea" ? (
                    <textarea data-testid={`fs-field-${k}`} rows={3} style={{ ...F.input, resize: "vertical" }} placeholder={String(baseVal(k) || "").slice(0, 160)} value={edit.patch[k] || ""} onChange={(e) => setEdit({ ...edit, patch: { ...edit.patch, [k]: e.target.value } })} />
                  ) : type === "photo" ? (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {(edit.patch[k] || baseVal(k)) && <img src={edit.patch[k] || baseVal(k)} alt="" style={{ width: 52, height: 40, borderRadius: 8, objectFit: "cover" }} />}
                      <input data-testid={`fs-field-${k}`} style={{ ...F.input, flex: 1 }} placeholder={String(baseVal(k) || "URL de l'image").slice(0, 80)} value={edit.patch[k] || ""} onChange={(e) => setEdit({ ...edit, patch: { ...edit.patch, [k]: e.target.value } })} />
                      <button style={F.act} data-testid={`fs-upload-${k}`} onClick={() => pickPhoto(k)}><Upload size={11} /> Téléverser</button>
                    </div>
                  ) : (
                    <input data-testid={`fs-field-${k}`} style={F.input} placeholder={String(baseVal(k) || "").slice(0, 120)} value={edit.patch[k] || ""} onChange={(e) => setEdit({ ...edit, patch: { ...edit.patch, [k]: e.target.value } })} />
                  )}
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button data-testid="fs-save" disabled={saving} onClick={save} style={{ ...F.tab(true), opacity: saving ? 0.6 : 1 }}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
                <button style={F.act} data-testid="fs-reset-modal" onClick={() => reset(edit.kind, edit.slug)}><RotateCcw size={11} /> Réinitialiser la fiche</button>
              </div>

              {/* Historique des modifications + retour arrière */}
              <div style={{ marginTop: 16, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }} data-testid="fs-versions">
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 900, color: "#EAF6EE" }}><History size={13} color="#2EE27B" /> Historique des modifications</div>
                {versions.length === 0 && <div style={{ fontSize: 11, color: "#8FB8A2", marginTop: 6 }}>Aucune modification enregistrée pour cette fiche.</div>}
                {versions.map((v) => (
                  <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }} data-testid={`fs-version-${v.id}`}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11.5, color: "#EAF6EE", fontWeight: 700 }}>
                        {v.action === "reset" ? "Réinitialisation" : v.action === "revert" ? "Retour arrière" : "Modification"} · {v.by}
                      </div>
                      <div style={{ fontSize: 10, color: "#8FB8A2" }}>{fmtDate(v.at)}{v.keys.length ? ` · champs : ${v.keys.join(", ")}` : ""}{v.hidden ? " · masquée" : ""}{v.boost ? " · en avant" : ""}</div>
                    </div>
                    <button style={F.act} data-testid={`fs-revert-${v.id}`} onClick={() => revert(v.id)}><RotateCcw size={11} /> Restaurer</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </InternalLayout>
  );
}
